import React from "react";
import styled from "styled-components";
import Window from "../Window";

// Display Properties: right-click the desktop → Properties. Period
// gesture, real effect — pick the wallpaper color, it sticks
// (localStorage). Swatches are era-plausible solid colors; patterns/
// tiled wallpapers could join later.
const WALLPAPERS = [
  { name: "Teal (default)", color: "#058284" },
  { name: "Desktop Green", color: "#3a6b3f" },
  { name: "Navy", color: "#1f3a63" },
  { name: "Slate", color: "#3a6ea5" },
  { name: "Plum", color: "#5a3d5c" },
  { name: "Clay", color: "#8a5a44" },
  { name: "Charcoal", color: "#3b3b3b" },
  { name: "Black", color: "#000000" },
];

const BG_KEY = "saat.desktopBg";

// localStorage throws outright when storage access is denied (Safari
// lockdown modes, blocked cookies) — the wallpaper is never worth
// taking the render down over.
function readStoredBg() {
  try {
    return localStorage.getItem(BG_KEY);
  } catch {
    return null;
  }
}

export function applyStoredDesktopBackground() {
  const stored = readStoredBg();
  if (stored) {
    document.documentElement.style.setProperty("--desktop-bg", stored);
  }
}

function DisplayProperties({ onClose, inactive = false }) {
  const [current, setCurrent] = React.useState(
    () =>
      readStoredBg() ||
      getComputedStyle(document.documentElement)
        .getPropertyValue("--desktop-bg")
        .trim(),
  );

  const choose = (color) => {
    setCurrent(color);
    document.documentElement.style.setProperty("--desktop-bg", color);
    try {
      localStorage.setItem(BG_KEY, color);
    } catch {
      // storage unavailable — session-only wallpaper is fine
    }
  };

  return (
    <StyledWindow
      title="Display Properties"
      inactive={inactive}
      controls={["close"]}
      onControlClick={(c) => c === "close" && onClose && onClose()}
    >
      <Body>
        <p>Background</p>
        <Swatches>
          {WALLPAPERS.map((w) => (
            <Swatch
              key={w.color}
              type="button"
              title={w.name}
              data-selected={current === w.color ? "true" : undefined}
              style={{ "--swatch": w.color }}
              onClick={() => choose(w.color)}
            />
          ))}
        </Swatches>
        <OkRow>
          <button type="button" onClick={onClose}>
            OK
          </button>
        </OkRow>
      </Body>
    </StyledWindow>
  );
}

const StyledWindow = styled(Window)`
  width: 300px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
`;

const Swatches = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const Swatch = styled.button`
  height: 44px;
  min-width: 0;
  padding: 0;
  background: var(--swatch);
  border: 1px solid #000;
  box-shadow: none;

  &&[data-selected="true"] {
    outline: 2px solid #00007b;
    outline-offset: 2px;
  }
  &&:not(:disabled):hover {
    box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
  &&:focus {
    outline: 2px dotted #00007b;
    outline-offset: 2px;
  }
`;

const OkRow = styled.div`
  display: flex;
  justify-content: flex-end;

  & button {
    min-width: 80px;
  }
`;

export default DisplayProperties;
